import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layers, Plus, Trash2, Save, GripVertical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  'Contabilidad': 'border-l-blue-500 bg-blue-50/30',
  'Electrónicos': 'border-l-amber-500 bg-amber-50/30',
  'Informes a Gerencia': 'border-l-emerald-500 bg-emerald-50/30',
};

const CATEGORY_BADGES = {
  'Contabilidad': 'bg-blue-100 text-blue-800',
  'Electrónicos': 'bg-amber-100 text-amber-800',
  'Informes a Gerencia': 'bg-emerald-100 text-emerald-800',
};

const ActivityTemplateManager = () => {
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [newActivity, setNewActivity] = useState({});

  const loadPlantillas = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/plantillas-actividades`);
      setPlantillas(data);
    } catch (err) {
      console.error('Error loading templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPlantillas(); }, [loadPlantillas]);

  const savePlantilla = async (plantilla) => {
    setSaving(plantilla._id);
    try {
      await axios.put(`${BACKEND_URL}/api/plantillas-actividades/${plantilla._id}`, {
        categoria: plantilla.categoria,
        actividades: plantilla.actividades,
      });
    } catch (err) {
      alert('Error al guardar');
    } finally {
      setSaving(null);
    }
  };

  const toggleActivity = (plantillaId, actIdx) => {
    setPlantillas(prev => prev.map(p => {
      if (p._id !== plantillaId) return p;
      const acts = [...p.actividades];
      acts[actIdx] = { ...acts[actIdx], activo: !acts[actIdx].activo };
      const updated = { ...p, actividades: acts };
      savePlantilla(updated);
      return updated;
    }));
  };

  const removeActivity = (plantillaId, actIdx) => {
    setPlantillas(prev => prev.map(p => {
      if (p._id !== plantillaId) return p;
      const acts = p.actividades.filter((_, i) => i !== actIdx);
      const updated = { ...p, actividades: acts };
      savePlantilla(updated);
      return updated;
    }));
  };

  const addActivity = (plantillaId) => {
    const name = (newActivity[plantillaId] || '').trim();
    if (!name) return;
    setPlantillas(prev => prev.map(p => {
      if (p._id !== plantillaId) return p;
      const acts = [...p.actividades, { nombre: name, activo: true }];
      const updated = { ...p, actividades: acts };
      savePlantilla(updated);
      return updated;
    }));
    setNewActivity(prev => ({ ...prev, [plantillaId]: '' }));
  };

  if (loading) return <p className="text-sm text-slate-500">Cargando plantillas...</p>;

  return (
    <div data-testid="activity-template-manager">
      <div className="flex items-center gap-2 mb-4">
        <Layers className="w-5 h-5 text-[#1F3864]" />
        <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Plantillas de Actividades
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Define las actividades estándar por categoría para el cierre mensual. La categoría <strong>Fiscales</strong> se genera automáticamente según los impuestos configurados de cada cliente.
      </p>

      {/* Fiscales info */}
      <div className="mb-4 border-l-4 border-l-red-500 bg-red-50/30 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-800">Fiscales</span>
          <span className="text-xs text-slate-500 italic">Automática</span>
        </div>
        <p className="text-xs text-slate-600">Se genera automáticamente a partir de los impuestos configurados en la pestaña "Impuestos" de cada cliente (ej: IVA, RETEICA — Cali, Retención en la Fuente Nacional).</p>
      </div>

      {/* Editable categories */}
      <div className="space-y-4">
        {plantillas.map((p) => (
          <div key={p._id} className={`border-l-4 rounded-lg p-4 ${CATEGORY_COLORS[p.categoria] || 'border-l-slate-400 bg-slate-50/30'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CATEGORY_BADGES[p.categoria] || 'bg-slate-100 text-slate-800'}`}>
                  {p.categoria}
                </span>
                <span className="text-xs text-slate-500">{p.actividades.filter(a => a.activo).length} activas</span>
              </div>
              {saving === p._id && <span className="text-xs text-blue-600">Guardando...</span>}
            </div>

            <div className="space-y-1.5">
              {p.actividades.map((act, idx) => (
                <div key={idx} className="flex items-center gap-2 group">
                  <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                  <button
                    onClick={() => toggleActivity(p._id, idx)}
                    className={`flex-1 text-left text-sm px-3 py-1.5 rounded transition-all ${
                      act.activo
                        ? 'text-slate-800 bg-white border border-slate-200'
                        : 'text-slate-400 bg-slate-100 border border-slate-100 line-through'
                    }`}
                    data-testid={`activity-toggle-${p.categoria}-${idx}`}
                  >
                    {act.nombre}
                  </button>
                  <button
                    onClick={() => removeActivity(p._id, idx)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-all"
                    data-testid={`activity-remove-${p.categoria}-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add new activity */}
            <div className="flex gap-2 mt-3">
              <input
                type="text"
                placeholder="Nueva actividad..."
                value={newActivity[p._id] || ''}
                onChange={(e) => setNewActivity(prev => ({ ...prev, [p._id]: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && addActivity(p._id)}
                className="flex-1 text-sm border border-slate-200 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1F3864]"
                data-testid={`activity-new-input-${p.categoria}`}
              />
              <button
                onClick={() => addActivity(p._id)}
                className="px-3 py-1.5 bg-[#1F3864] text-white text-xs rounded hover:bg-[#172b4d] transition-colors flex items-center gap-1"
                data-testid={`activity-add-btn-${p.categoria}`}
              >
                <Plus className="w-3.5 h-3.5" />
                Agregar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityTemplateManager;
